import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { SectionType } from 'app/library/section/enums/section-type.enum';
import { FeedbackSection } from 'app/library/section/models/feedback-section.model';
import { FilesSection } from 'app/library/section/models/files-section.model';
import { IntegratedQuizSection } from 'app/library/section/models/integrated-quiz-section.model';
import { OneFileSection } from 'app/library/section/models/one-file-section.model';
import { QuizSection } from 'app/library/section/models/quiz-section.model';
import { RecommendationsSection } from 'app/library/section/models/recommendations-section.model';
import { Section } from 'app/library/section/models/section.model';
import { SurveySection } from 'app/library/section/models/survey-section.model';
import { TextSection } from 'app/library/section/models/text-section.model';
import { VideoSection } from 'app/library/section/models/video-section.model';
import { IEnvironment } from 'app/projects/core/src/lib/interfaces/environment.interface';
import { MAXBRAIN_ENVIRONMENT } from 'app/projects/core/src/lib/services/environment.token';
import { User } from 'app/projects/user/src/lib/models/user';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ELearningSection } from '../../section/models/eLearning-section.model';
import { IModuleCompletionSection } from '../interfaces/module-completion-section.interface';
import { ISectionStats } from '../interfaces/module-stats.interface';
import { UserSectionStats } from '../models/user-section.model';

export interface IUserSectionStatsAndPercentage {
    statsParticipants: UserSectionStats[];
    statsPercentage: number;
    totalRows?: number;

    pagination: {
        number_of_pages: number;
        number_of_results: number;
    };
}
export interface Pagination {
    numberOfPages: number;
    numberOfResults: number;
}

@Injectable()
export class UserSectionApiService {
    private _participantsPagination: Pagination = { numberOfPages: 0, numberOfResults: 0 };

    get participantsPagination(): Pagination {
        return this._participantsPagination;
    }
    set participantsPagination(value: Pagination) {
        this._participantsPagination = value;
    }

    showSpinner = false;

    participantsPageIndex = 1;

    constructor(private http: HttpClient, @Inject(MAXBRAIN_ENVIRONMENT) private environment: IEnvironment) {}

    getModuleStats(
        moduleId: string,
        pageSize = 10,
        pageIndex = 1,
        sortBy?: string,
        sortOrder?: string,
        search?: string,
        status?: string,
        sectionId?: string
    ): Observable<IUserSectionStatsAndPercentage> {
        this.showSpinner = true;
        this.participantsPageIndex = pageIndex;
        let params = new HttpParams()
            .set('page', pageIndex.toString())
            .set('limit', pageSize.toString())
            .set('sortBy', sortBy || 'name')
            .set('sortOrder', sortOrder || 'asc');

        if (search) {
            params = params.set('search', search);
        }

        if (status) {
            params = params.set('status', status);
        }

        if (sectionId) {
            params = params.set('filter[section]', sectionId);
        }

        return this.http.get<ISectionStats>(`${this.environment.apiUrl}/modules/${moduleId}/section-stats`, { params }).pipe(
            map((payload) => {
                const statsParticipants: UserSectionStats[] = [];

                Object.values(payload.participants).forEach((participant) => {
                    const stats = new UserSectionStats(new User(participant.user), participant.marked, participant.total);

                    Object.values(participant.sections).forEach((section) => {
                        let tempSection: Section;

                        switch (section.section.type) {
                            case SectionType.Files:
                                tempSection = new FilesSection(section.section);
                                break;
                            case SectionType.Text:
                                tempSection = new TextSection(section.section);
                                break;
                            case SectionType.Feedback:
                                tempSection = new FeedbackSection(section.section);
                                break;
                            case SectionType.Quiz:
                                tempSection = new QuizSection(section.section);
                                break;
                            case SectionType.IntegratedQuiz:
                                tempSection = new IntegratedQuizSection(section.section);
                                break;
                            case SectionType.ELearning:
                                tempSection = new ELearningSection(section.section);
                                break;
                            case SectionType.Survey:
                                tempSection = new SurveySection(section.section);
                                break;
                            case SectionType.OneFile:
                                tempSection = new OneFileSection(section.section);
                                break;
                            case SectionType.Video:
                                tempSection = new VideoSection(section.section);
                                break;
                            case SectionType.Recommendations:
                                tempSection = new RecommendationsSection(section.section);
                                break;
                        }

                        stats.sectionStats.push({ section: tempSection, completed: section.marked });
                    });

                    statsParticipants.push(stats);
                });

                this.showSpinner = false;

                this.participantsPagination = {
                    numberOfPages: payload.pagination.number_of_pages,
                    numberOfResults: payload.pagination.number_of_results,
                };

                return {
                    statsParticipants,
                    statsPercentage: payload.percentage,
                    pagination: payload.pagination,
                    totalRows: statsParticipants.length,
                };
            })
        );
    }

    getModuleStatsForParticipant(moduleId: string, participantId: string): Observable<IUserSectionStatsAndPercentage> {
        this.showSpinner = true;

        let params = new HttpParams().set('filter[participant]', participantId);

        return this.http.get<ISectionStats>(`${this.environment.apiUrl}/modules/${moduleId}/section-stats`, { params }).pipe(
            map((payload) => {
                const statsParticipants: UserSectionStats[] = [];

                Object.values(payload.participants).forEach((participant) => {
                    const stats = new UserSectionStats(new User(participant.user), participant.marked, participant.total);

                    Object.values(participant.sections).forEach((section) => {
                        let tempSection: Section;

                        switch (section.section.type) {
                            case SectionType.Files:
                                tempSection = new FilesSection(section.section);
                                break;
                            case SectionType.Text:
                                tempSection = new TextSection(section.section);
                                break;
                            case SectionType.Feedback:
                                tempSection = new FeedbackSection(section.section);
                                break;
                            case SectionType.Quiz:
                                tempSection = new QuizSection(section.section);
                                break;
                            case SectionType.IntegratedQuiz:
                                tempSection = new IntegratedQuizSection(section.section);
                                break;
                            case SectionType.ELearning:
                                tempSection = new ELearningSection(section.section);
                                break;
                            case SectionType.Survey:
                                tempSection = new SurveySection(section.section);
                                break;
                            case SectionType.OneFile:
                                tempSection = new OneFileSection(section.section);
                                break;
                            case SectionType.Video:
                                tempSection = new VideoSection(section.section);
                                break;
                            case SectionType.Recommendations:
                                tempSection = new RecommendationsSection(section.section);
                                break;
                        }
                        tempSection.timeSpent = section.time_spent;
                        tempSection.visitedCount = section.visited_count;
                        stats.sectionStats.push({ section: tempSection, completed: section.marked });
                    });

                    statsParticipants.push(stats);
                });

                this.showSpinner = false;

                this.participantsPagination = {
                    numberOfPages: payload.pagination.number_of_pages,
                    numberOfResults: payload.pagination.number_of_results,
                };

                return {
                    statsParticipants,
                    statsPercentage: payload.percentage,
                    pagination: payload.pagination,
                    totalRows: statsParticipants.length,
                };
            }),
            catchError((error) => this.errorHandler(error))
        );
    }

    bulkSectionCompletion(sections: IModuleCompletionSection[], userId: number): Observable<IUserSectionStatsAndPercentage> {
        const data = {
            user_id: userId,
            sections,
        };

        return this.http.post<ISectionStats>(`${this.environment.apiUrl}/sections/completion`, data).pipe(
            map((payload) => {
                const statsParticipants: UserSectionStats[] = [];

                Object.values(payload.participants).forEach((participant) => {
                    const stats = new UserSectionStats(new User(participant.user), participant.marked, participant.total);

                    Object.values(participant.sections).forEach((section) => {
                        let tempSection: Section;

                        switch (section.section.type) {
                            case SectionType.Files:
                                tempSection = new FilesSection(section.section);
                                break;
                            case SectionType.Text:
                                tempSection = new TextSection(section.section);
                                break;
                            case SectionType.Feedback:
                                tempSection = new FeedbackSection(section.section);
                                break;
                            case SectionType.IntegratedQuiz:
                                tempSection = new IntegratedQuizSection(section.section);
                                break;
                            case SectionType.ELearning:
                                tempSection = new ELearningSection(section.section);
                                break;
                            case SectionType.Survey:
                                tempSection = new SurveySection(section.section);
                                break;
                            case SectionType.OneFile:
                                tempSection = new OneFileSection(section.section);
                                break;
                            case SectionType.Video:
                                tempSection = new VideoSection(section.section);
                                break;
                            case SectionType.Recommendations:
                                tempSection = new RecommendationsSection(section.section);
                                break;
                        }
                        tempSection.timeSpent = section.time_spent;
                        tempSection.visitedCount = section.visited_count;
                        stats.sectionStats.push({ section: tempSection, completed: section.marked });
                    });

                    statsParticipants.push(stats);
                });

                this.showSpinner = false;

                this.participantsPagination = {
                    numberOfPages: payload.pagination.number_of_pages,
                    numberOfResults: payload.pagination.number_of_results,
                };

                return {
                    statsParticipants,
                    statsPercentage: payload.percentage,
                    pagination: payload.pagination,
                    totalRows: statsParticipants.length,
                };
            }),
            catchError(this.errorHandler)
        );
    }

    errorHandler(error: any): Observable<never> {
        console.error('An error occurred:', error);
        return throwError(() => new Error(error));
    }
}
